import { useEffect, useMemo, useState } from 'react'
import type { AnalysisTimeRangeKey } from '../../../../types/analysis'
import { subscribePlanProposals, type AiPlanProposal } from '../../../../services/aiAgentBridge'
import type { LiveModuleHeadline, LiveModuleRecord, LiveModuleSparkline } from '../LiveModuleCard'
import { bucketize, calculateDelta, timeRangeToDateRange } from './bucketize'

export interface UseProposalUsageModuleParams {
  workspaceId: string
  uid?: string | null
  canApprove?: boolean
  timeRange: AnalysisTimeRangeKey
  refreshKey?: number
  now?: Date
}

export interface UseProposalUsageModuleResult {
  headline: LiveModuleHeadline
  sparkline: LiveModuleSparkline
  records: LiveModuleRecord[]
  loading: boolean
  error: Error | null
}

const parseDate = (val: unknown): Date | null => {
  if (!val) return null
  if (val instanceof Date) return val
  if (typeof (val as { toDate?: () => Date }).toDate === 'function') {
    return (val as { toDate: () => Date }).toDate()
  }
  if (typeof val === 'string' || typeof val === 'number') {
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d
  }
  return null
}

export const useProposalUsageModule = ({
  workspaceId,
  uid,
  canApprove = true,
  timeRange,
  refreshKey,
  now = new Date(),
}: UseProposalUsageModuleParams): UseProposalUsageModuleResult => {
  const [proposals, setProposals] = useState<AiPlanProposal[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!workspaceId || !uid) {
      setProposals([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const unsubscribe = subscribePlanProposals({
      workspaceId,
      uid,
      canApprove,
      onChange: (list) => {
        setProposals(list)
        setLoading(false)
      },
      onError: (err) => {
        setError(err)
        setLoading(false)
      },
    })

    return () => {
      unsubscribe()
    }
  }, [workspaceId, uid, canApprove, refreshKey])

  return useMemo(() => {
    const { from, to } = timeRangeToDateRange(timeRange, now)

    const inWindow = proposals.filter((p) => {
      const d = parseDate(p.createdAt)
      return d && d >= from && d <= to
    })

    const totalCount = inWindow.length

    const createdDates = inWindow
      .map((p) => parseDate(p.createdAt))
      .filter((d): d is Date => d !== null)

    const points = bucketize(createdDates, timeRange, now)
    const delta = calculateDelta(points)

    const topRecords: LiveModuleRecord[] = [...inWindow]
      .sort((a, b) => {
        const da = parseDate(a.createdAt)?.getTime() ?? 0
        const db = parseDate(b.createdAt)?.getTime() ?? 0
        return db - da
      })
      .slice(0, 5)
      .map((p, index) => {
        const title = p.title?.trim() || 'Untitled proposal'
        const status = p.status || 'PENDING'
        const actor = p.createdBy?.trim() || 'AI'
        return {
          id: p.id || `proposal-usage-${index}`,
          primary: title,
          secondary: `${actor} · ${status}`,
          numeric: p.creditCost ?? 0,
          drillTarget: 'SUMMARY',
        }
      })

    const headline: LiveModuleHeadline = totalCount === 0
      ? { value: '—', label: 'no records' }
      : { value: totalCount, delta, label: 'proposals' }

    return {
      headline,
      sparkline: { points, direction: 'up-is-good' },
      records: topRecords,
      loading,
      error,
    }
  }, [proposals, timeRange, now, loading, error])
}
